import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Typography,
  IconButton,
  Alert,
  LinearProgress,
  Fade,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import {
  Upload,
  FileText,
  Plus,
  Paperclip,
  Trash2,
} from 'lucide-react';
import CustomButton from '../ui/CustomButton';

const DirectCOCUploadDialog = ({
  open,
  onClose,
  onUpload,
  isDarkMode = false,
}) => {
  const [cocNumber, setCocNumber] = useState('');
  const [files, setFiles] = useState([]);
  const [titles, setTitles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Reset form when dialog opens/closes
  React.useEffect(() => {
    if (!open) {
      handleReset();
    }
  }, [open]);

  const handleReset = () => {
    setCocNumber('');
    setFiles([]);
    setTitles([]);
    setErrors({});
    setIsUploading(false);
    setDragOver(false);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!cocNumber.trim()) {
      newErrors.cocNumber = 'COC Number is required';
    }

    if (files.length === 0) {
      newErrors.files = 'At least one file is required';
    }

    // Check for empty titles
    titles.forEach((title, index) => {
      if (!title.trim()) {
        newErrors[`title_${index}`] = 'Title is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileSelect = selectedFiles => {
    const fileArray = Array.from(selectedFiles);
    const newFiles = [...files, ...fileArray];
    const newTitles = [
      ...titles,
      ...fileArray.map(file => file.name.split('.')[0]),
    ];

    setFiles(newFiles);
    setTitles(newTitles);
    setErrors(prev => ({ ...prev, files: null }));
  };

  const handleFileInputChange = event => {
    handleFileSelect(event.target.files);
  };

  const handleDragEnter = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleRemoveFile = index => {
    const newFiles = files.filter((_, i) => i !== index);
    const newTitles = titles.filter((_, i) => i !== index);
    setFiles(newFiles);
    setTitles(newTitles);

    // Remove specific title error
    const newErrors = { ...errors };
    delete newErrors[`title_${index}`];
    setErrors(newErrors);
  };

  const handleTitleChange = (index, value) => {
    const newTitles = [...titles];
    newTitles[index] = value;
    setTitles(newTitles);

    // Clear title error when user types
    if (value.trim()) {
      const newErrors = { ...errors };
      delete newErrors[`title_${index}`];
      setErrors(newErrors);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsUploading(true);
      await onUpload(cocNumber.trim(), files, titles);
      handleReset();
      onClose();
    } catch (error) {
      console.error('Upload error:', error);
      setErrors({ submit: error.message || 'Upload failed' });
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = bytes => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          boxShadow: isDarkMode
            ? '0 25px 50px rgba(0, 0, 0, 0.5)'
            : '0 25px 50px rgba(0, 0, 0, 0.15)',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Upload size={24} />
        Upload Attachments by COC Number
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {isUploading && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress
              sx={{
                borderRadius: 1,
                height: 8,
                background: isDarkMode ? '#1e293b' : '#f1f5f9',
                '& .MuiLinearProgress-bar': {
                  background:
                    'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                textAlign: 'center',
                color: isDarkMode ? '#94a3b8' : '#64748b',
              }}
            >
              Uploading files...
            </Typography>
          </Box>
        )}

        {errors.submit && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {errors.submit}
          </Alert>
        )}

        {/* COC Number Input */}
        <Box sx={{ mb: 3 }}>
          <TextField
            label="COC Number"
            fullWidth
            margin="normal"
            value={cocNumber}
            onChange={e => {
              setCocNumber(e.target.value);
              if (e.target.value.trim()) {
                setErrors(prev => ({ ...prev, cocNumber: null }));
              }
            }}
            placeholder="Enter the certificate COC number (e.g., BC55555)"
            error={!!errors.cocNumber}
            helperText={errors.cocNumber}
            disabled={isUploading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                background: isDarkMode
                  ? 'rgba(30, 41, 59, 0.5)'
                  : 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.7)'
                    : 'rgba(255, 255, 255, 0.7)',
                },
                '&.Mui-focused': {
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.8)'
                    : 'rgba(255, 255, 255, 0.8)',
                },
              },
            }}
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* File Upload Area */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: isDarkMode ? '#f1f5f9' : '#1e293b',
              mb: 2,
            }}
          >
            Attachments {files.length > 0 && `(${files.length})`}
          </Typography>

          {/* Drag & Drop Area */}
          <Paper
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              p: 4,
              borderRadius: 2,
              border: `2px dashed ${dragOver ? '#3b82f6' : isDarkMode ? '#374151' : '#d1d5db'}`,
              background: dragOver
                ? isDarkMode
                  ? 'rgba(59, 130, 246, 0.1)'
                  : 'rgba(59, 130, 246, 0.05)'
                : isDarkMode
                  ? 'rgba(30, 41, 59, 0.3)'
                  : 'rgba(248, 250, 252, 0.5)',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              '&:hover': {
                border: `2px dashed #3b82f6`,
                background: isDarkMode
                  ? 'rgba(59, 130, 246, 0.1)'
                  : 'rgba(59, 130, 246, 0.05)',
              },
            }}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileInputChange}
              multiple
              style={{ display: 'none' }}
              disabled={isUploading}
            />

            <Upload
              size={48}
              color={dragOver ? '#3b82f6' : isDarkMode ? '#64748b' : '#94a3b8'}
              style={{ marginBottom: 16 }}
            />

            <Typography
              variant="h6"
              sx={{
                color: dragOver
                  ? '#3b82f6'
                  : isDarkMode
                    ? '#e2e8f0'
                    : '#374151',
                mb: 1,
              }}
            >
              {dragOver ? 'Drop files here' : 'Upload Files'}
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', mb: 2 }}
            >
              Drag and drop files here or click to select
            </Typography>

            <CustomButton
              variant="outlined"
              startIcon={<Plus size={16} />}
              disabled={isUploading}
              sx={{
                borderColor: isDarkMode ? '#374151' : '#d1d5db',
                color: isDarkMode ? '#e5e7eb' : '#374151',
                '&:hover': {
                  borderColor: '#3b82f6',
                  color: '#3b82f6',
                },
              }}
            >
              Select Files
            </CustomButton>
          </Paper>

          {errors.files && (
            <Typography
              variant="caption"
              sx={{ color: '#ef4444', mt: 1, display: 'block' }}
            >
              {errors.files}
            </Typography>
          )}
        </Box>

        {/* Selected Files List */}
        {files.length > 0 && (
          <Fade in={true}>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? '#cbd5e1' : '#475569',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Paperclip size={16} />
                Selected Files & Titles
              </Typography>

              <List
                sx={{
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.3)'
                    : 'rgba(248, 250, 252, 0.5)',
                  borderRadius: 2,
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                {files.map((file, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      border: isDarkMode
                        ? '1px solid rgba(148, 163, 184, 0.1)'
                        : '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: 1,
                      mb: 1,
                      background: isDarkMode
                        ? 'rgba(30, 41, 59, 0.5)'
                        : 'rgba(255, 255, 255, 0.5)',
                    }}
                  >
                    <ListItemIcon>
                      <FileText
                        size={20}
                        color={isDarkMode ? '#94a3b8' : '#64748b'}
                      />
                    </ListItemIcon>

                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {file.name} ({formatFileSize(file.size)})
                          </Typography>
                          <TextField
                            label="Title"
                            size="small"
                            fullWidth
                            value={titles[index] || ''}
                            onChange={e =>
                              handleTitleChange(index, e.target.value)
                            }
                            placeholder="Enter title for this attachment"
                            error={!!errors[`title_${index}`]}
                            helperText={errors[`title_${index}`]}
                            disabled={isUploading}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1,
                                background: isDarkMode
                                  ? 'rgba(15, 23, 42, 0.5)'
                                  : 'rgba(255, 255, 255, 0.8)',
                              },
                            }}
                          />
                        </Box>
                      }
                    />

                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveFile(index)}
                        disabled={isUploading}
                        sx={{
                          color: '#ef4444',
                          '&:hover': {
                            background: 'rgba(239, 68, 68, 0.1)',
                          },
                        }}
                      >
                        <Trash2 size={18} />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Fade>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          background: isDarkMode
            ? 'rgba(15, 23, 42, 0.5)'
            : 'rgba(248, 250, 252, 0.5)',
          gap: 2,
        }}
      >
        <CustomButton
          variant="outlined"
          onClick={onClose}
          disabled={isUploading}
          sx={{
            borderColor: isDarkMode ? '#374151' : '#d1d5db',
            color: isDarkMode ? '#e5e7eb' : '#374151',
          }}
        >
          Cancel
        </CustomButton>

        <CustomButton
          variant="gradient"
          gradient="primary"
          onClick={handleSubmit}
          disabled={isUploading || files.length === 0 || !cocNumber.trim()}
          startIcon={
            isUploading ? (
              <div className="animate-spin">
                <Upload size={16} />
              </div>
            ) : (
              <Upload size={16} />
            )
          }
          sx={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
              boxShadow: '0 8px 25px rgba(59, 130, 246, 0.6)',
              transform: 'translateY(-2px)',
            },
            '&:disabled': {
              opacity: 0.6,
              transform: 'none',
            },
          }}
        >
          {isUploading ? 'Uploading...' : `Upload ${files.length} Files`}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default DirectCOCUploadDialog;
